import { type ReactNode } from 'react';
import { Camera, Image, Mail } from 'lucide-react';
import { StoreHoursCard, StoreInfoCard } from '@ampersandbrands/ui';
import { storeLabel, useStoreMe, useEffectiveStoreNo, isNotAvailable } from '@/lib/useStores';
import { useStoreDetails, type StoreDetails } from '@/lib/useStoreDetails';

// --- Page head ---

function PageHead({
  storeName,
  storeStatus,
}: {
  storeName?: string;
  storeStatus?: string | null;
}) {
  const statusColor =
    storeStatus === 'open'
      ? 'bg-candy-teal/10 text-candy-teal dark:text-candy-teal-dark'
      : storeStatus === 'closed'
        ? 'bg-candy-ink-muted/10 text-candy-ink-muted dark:text-candy-dark-ink-muted'
        : 'bg-candy-gold/10 text-candy-gold dark:text-candy-gold-dark';

  return (
    <div className="mb-5">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-2xl font-semibold text-candy-ink dark:text-candy-dark-ink">
          {storeName ?? 'My Store'}
        </h1>
        {storeStatus && (
          <span
            className={`rounded-pill px-2.5 py-1 text-[12px] font-medium capitalize ${statusColor}`}
          >
            {storeStatus}
          </span>
        )}
      </div>
      <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Store details
      </p>
    </div>
  );
}

// --- Links row (same external-link treatment as Home's Store links tiles) ---

function ExternalLinkTile({
  icon,
  label,
  href,
}: {
  icon: ReactNode;
  label: string;
  href?: string;
}) {
  const base =
    'flex flex-col items-center gap-2 rounded-xl border border-candy-line bg-candy-white px-3 py-4 text-center shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface';
  const iconWrap =
    'flex h-11 w-11 items-center justify-center rounded-full bg-candy-cream-warm text-candy-pink dark:bg-candy-dark-surface-warm dark:text-candy-pink-dark';
  const labelCls = 'text-[13px] font-medium text-candy-ink dark:text-candy-dark-ink';

  if (href) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className={`${base} hover:bg-candy-cream-warm dark:hover:bg-candy-dark-surface-warm`}
      >
        <span className={iconWrap}>{icon}</span>
        <span className={labelCls}>{label}</span>
      </a>
    );
  }

  return (
    <div
      className={`${base} cursor-not-allowed opacity-60`}
      title="Not set up for this store yet"
      aria-disabled="true"
    >
      <span className={iconWrap}>{icon}</span>
      <span className={labelCls}>{label}</span>
    </div>
  );
}

function LinksRow({
  editable,
  loading,
}: {
  editable: StoreDetails['editable'] | undefined;
  loading: boolean;
}) {
  if (loading) {
    return (
      <div className="grid grid-cols-3 gap-3">
        {([0, 1, 2] as const).map((i) => (
          <div
            key={i}
            className="h-24 animate-pulse rounded-xl bg-candy-cream-warm dark:bg-candy-dark-surface-warm"
          />
        ))}
      </div>
    );
  }

  const gmailHref = editable?.gmail_address
    ? `https://accounts.google.com/AccountChooser?Email=${encodeURIComponent(editable.gmail_address)}&continue=${encodeURIComponent('https://mail.google.com/mail/')}`
    : undefined;

  return (
    <div>
      <div className="mb-2 text-xs font-medium uppercase tracking-wide text-candy-ink-muted dark:text-candy-dark-ink-muted">
        Store links
      </div>
      <div className="grid grid-cols-3 gap-3">
        <ExternalLinkTile
          icon={<Camera size={22} aria-hidden />}
          label="Cameras"
          href={editable?.camera_url ?? undefined}
        />
        <ExternalLinkTile
          icon={<Image size={22} aria-hidden />}
          label="Store Photos"
          href={editable?.photos_url ?? undefined}
        />
        <ExternalLinkTile
          icon={<Mail size={22} aria-hidden />}
          label="Store Gmail"
          href={gmailHref}
        />
      </div>
    </div>
  );
}

// --- Page export ---

export function MyStore() {
  const activeStoreNo = useEffectiveStoreNo();

  const meQ = useStoreMe(activeStoreNo);
  const detailsQ = useStoreDetails(activeStoreNo);

  const store = meQ.data?.store;
  const storeName = store ? storeLabel(store.store_no, store.store_name) : undefined;
  const storeStatus = detailsQ.data?.editable?.store_status;

  const isLoading = meQ.isLoading || detailsQ.isLoading;

  if (isLoading) {
    return (
      <div className="mx-auto w-full max-w-lg pb-24">
        <div className="mb-5 h-10 w-48 animate-pulse rounded-lg bg-candy-cream-warm dark:bg-candy-dark-surface-warm" />
        <div className="space-y-4">
          {([0, 1, 2] as const).map((i) => (
            <div
              key={i}
              className="h-32 animate-pulse rounded-xl bg-candy-cream-warm dark:bg-candy-dark-surface-warm"
            />
          ))}
        </div>
      </div>
    );
  }

  const loadErr = meQ.error ?? detailsQ.error;
  if (loadErr && !isNotAvailable(loadErr)) {
    return (
      <div className="mx-auto w-full max-w-lg pb-24">
        <PageHead storeName={storeName} />
        <div className="rounded-xl border border-candy-line bg-candy-white px-4 py-6 text-center shadow-1 dark:border-candy-dark-line dark:bg-candy-dark-surface">
          <p className="text-sm text-candy-ink-muted dark:text-candy-dark-ink-muted">
            Could not load store details. Please try again.
          </p>
        </div>
      </div>
    );
  }

  const details = detailsQ.data ?? null;

  return (
    <div className="mx-auto w-full max-w-lg pb-24">
      <PageHead storeName={storeName} storeStatus={storeStatus} />

      <div className="space-y-4">
        <StoreInfoCard details={details} />
        <StoreHoursCard details={details} />
        <LinksRow editable={details?.editable} loading={false} />
      </div>
    </div>
  );
}
